import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { updateEntity, useEntityList } from '../../data/entities';
import { REQUIREMENT_KINDS } from '../../data/requirements';
import { StatusChip } from '../RequirementsEditor';
import { pipelineVerdict, useOrgPipelines } from '../SolutionBoard';

// A story's acceptance line becomes a design requirement on an nQube
// workflow ([[solution-engineering]]): the row carries `storyId`, so the
// canvas verdict reads back here and the story shows which of its criteria
// the workflow design already guarantees.

const newId = () => `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

export function useStoryRequirements(org) {
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const { pipelines, feeds } = useOrgPipelines(org.id, workspaces);
  return { pipelines, feeds };
}

// The requirement rows (across every workflow) that trace to this story,
// with their live verdicts.
export function linkedRequirements(org, pipelines, storyId) {
  const out = [];
  for (const pl of pipelines) {
    if (!(pl.requirements || []).some((r) => r.storyId === storyId)) continue;
    const v = pipelineVerdict(org, pl);
    for (const r of v.results.filter((x) => x.storyId === storyId)) out.push({ ...r, pipeline: pl });
  }
  return out;
}

export function MakeRequirement({ org, app, story, criterion, pipelines, act, busy }) {
  const [open, setOpen] = useState(false);
  const [plId, setPlId] = useState(app?.links?.pipelineId || pipelines[0]?.id || '');
  const [kind, setKind] = useState('approval');
  const pl = pipelines.find((p) => p.id === plId) || null;
  const already = pipelines.some((p) => (p.requirements || []).some((r) => r.storyId === story.id && r.text === criterion));
  if (already) return <span className="mf-muted lab-step-sub" style={{ paddingLeft: 0, display: 'block' }} data-nova-note={`Acceptance line "${criterion.slice(0, 90)}" is already a workflow requirement.`}>a workflow requirement</span>;
  if (!pipelines.length) return null;
  if (!open) return <button type="button" className="cs-btn-link mf-muted lab-step-sub" style={{ paddingLeft: 0, display: 'block' }} data-nova-note={`Acceptance line "${criterion.slice(0, 90)}" can become a workflow requirement (declare_requirement with storyId ${story.id}).`} onClick={() => setOpen(true)}>make this a workflow requirement</button>;
  return (
    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
      <select className="cs-input" style={{ fontSize: 12 }} value={plId} onChange={(e) => setPlId(e.target.value)} aria-label="Workflow">
        {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select className="cs-input" style={{ fontSize: 12 }} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Requirement kind">
        {Object.entries(REQUIREMENT_KINDS).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
      </select>
      <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!pl || !!busy} onClick={() => act(`req-${story.id}`, async () => {
        await updateEntity('pipeline', { org: org.id, workspace: pl.workspaceId }, pl.id, {
          requirements: [...(pl.requirements || []), { id: newId(), kind, text: criterion, params: {}, source: 'pipeline', storyId: story.id }],
        });
        setOpen(false);
      })}>Add</button>
      <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOpen(false)}>Cancel</button>
    </span>
  );
}

export function LinkedRequirements({ org, pipelines, story }) {
  const rows = linkedRequirements(org, pipelines, story.id);
  if (!rows.length) return null;
  return (
    <section className="lab-section">
      <h4 className="lab-h">Workflow requirements from this story</h4>
      {rows.map((r) => (
        <div key={`${r.pipeline.id}-${r.id}`} className={`nq-req is-${r.status}`} style={{ marginTop: 6 }} data-nova-note={`Requirement "${r.text.slice(0, 80)}" on workflow ${r.pipeline.name} is ${r.status === 'unmet' ? 'NOT met' : r.status === 'met' ? 'met' : 'waiting for a run'}: ${String(r.detail || '').slice(0, 90)}`}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <StatusChip status={r.status} />
            <strong style={{ fontSize: 12, flex: 1 }}>{r.text}</strong>
            <Link to={`/orgs/${org.id}/ws/${r.pipeline.workspaceId}/pipelines/${r.pipeline.id}`} className="mf-muted" style={{ fontSize: 12 }}>{r.pipeline.name} →</Link>
          </div>
          <div className="cs-help" style={{ fontSize: 11.5, marginTop: 2 }}>{r.detail}</div>
        </div>
      ))}
    </section>
  );
}
